// /plans (spec "Public launch"): pick a size and monthly or yearly, add a code, then pay in
// Stripe Checkout; the machine is made once it's paid (back on Getting ready). The sizes, the
// interval switch and the price come from here for the dashboard's Change plan too.

import { useEffect, useState, type ReactNode } from "react";
import {
  billing,
  codeOff,
  rememberedCode,
  usd,
  type Code,
  type Interval,
  type PlanOption,
} from "../api";
import { Link, Redirect, useLocation } from "../router";
import { useSession } from "../session";
import { PageSplash, signOut } from "./Shell";
import { HxPage, Icon } from "./hx";
import { PLAN_ART } from "./planArt";
import { Segmented } from "../ui/ds";

/** What a size costs for an interval, after a code (cents). */
export function priceOf(p: PlanOption, interval: Interval, code?: Code | null) {
  const full = interval === "year" ? p.yearly : p.monthly;
  const fits = code && (!code.tier || code.tier === p.id);
  return { full, now: fits ? Math.round((full * (100 - code!.percentOff)) / 100) : full };
}

/** Monthly or yearly, with what yearly saves. */
export function IntervalSwitch({ value, onChange }: { value: Interval; onChange: (v: Interval) => void }) {
  return (
    <Segmented<Interval>
      label="Billing"
      value={value}
      onChange={onChange}
      items={[
        { value: "month", label: "Monthly", testid: "interval-month" },
        { value: "year", label: "Yearly", testid: "interval-year", suffix: <span className="hx-seg-badge">2 months free</span> },
      ]}
    />
  );
}

/** The sizes as cards to pick from. current: the account's size (marked, not picked again). */
export function SizeCards({
  plans,
  tier,
  interval,
  code,
  current,
  art = false,
  onPick,
}: {
  plans: PlanOption[];
  tier: string;
  interval: Interval;
  code?: Code | null;
  current?: string;
  /** The machine for the size above each card (the Plans page; the dashboard's rows have none). */
  art?: boolean;
  onPick: (id: string) => void;
}) {
  return (
    <div className="hx-sizes" role="radiogroup" aria-label="Size" data-testid="sizes">
      {plans.map((p, i) => {
        const { full, now } = priceOf(p, interval, code);
        const full_ = !p.available && p.id !== current;
        return (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={tier === p.id}
            className="hx-size hx-rise"
            style={{ ["--i" as string]: i + 1 }}
            data-full={full_}
            onClick={() => onPick(p.id)}
            data-testid={`size-${p.id}`}
          >
            {art && PLAN_ART[p.id.split("-")[1]] && (
              <span className="hx-size-art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: PLAN_ART[p.id.split("-")[1]] }} />
            )}
            <span className="hx-size-head">
              <b>{p.name}</b>
              {p.id === current ? (
                <span className="hx-start-way-badge">Your plan</span>
              ) : full_ ? (
                <span className="hx-start-way-badge is-full">Full right now</span>
              ) : p.id === "standard-m" ? (
                <span className="hx-start-way-badge is-recommended">Most picked</span>
              ) : null}
              <span className="hx-start-way-dot" aria-hidden="true" />
            </span>
            <span className="hx-size-price" key={interval + now}>
              {now !== full && <s className="hx-faint">{usd(full)}</s>}
              <span className="hx-size-amt">{usd(now)}</span>
              <span className="hx-faint">/{interval === "year" ? "year" : "month"}</span>
            </span>
            <span className="hx-size-specs">
              <span>{p.vcpu} vCPU</span>
              <span>{p.memoryGB} GB memory</span>
              <span>{p.diskGB} GB disk</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** "Have a code?": checks it as it's applied and hands it up. */
function CodeField({ code, onCode }: { code: Code | null; onCode: (c: Code | null) => void }) {
  const [open, setOpen] = useState(!!rememberedCode());
  const [text, setText] = useState(rememberedCode());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apply = async (t = text) => {
    if (!t.trim()) return;
    setBusy(true);
    setError(null);
    const r = await billing.code(t.trim());
    setBusy(false);
    if (r.ok) onCode(r.data.code);
    else (setError(r.error), onCode(null));
  };
  useEffect(() => {
    if (text) void apply(text); // a code from the sign-up link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (code)
    return (
      <p className="hx-code-applied" data-testid="code-applied">
        <span className="hx-check">{Icon.check}</span>
        <span>
          <b>{code.code}</b> · {codeOff(code)}
        </span>
        <button type="button" className="hx-linkbtn" onClick={() => (onCode(null), setText(""))}>
          Remove
        </button>
      </p>
    );
  if (!open)
    return (
      <button type="button" className="hx-linkbtn hx-code-open" onClick={() => setOpen(true)} data-testid="code-open">
        Have a code?
      </button>
    );
  return (
    <form className="hx-code-form" onSubmit={(e) => (e.preventDefault(), void apply())} data-testid="code-form">
      <input
        aria-label="Code"
        placeholder="Code"
        value={text}
        autoFocus
        autoCapitalize="characters"
        onChange={(e) => (setText(e.target.value.toUpperCase()), setError(null))}
        data-testid="code-input"
      />
      <button className="hx-btn hx-btn-secondary hx-btn-sm" type="submit" disabled={busy || !text.trim()}>
        {busy ? "Checking…" : "Apply"}
      </button>
      {error && (
        <span className="hx-error" role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

export function PlansPage() {
  const { auth, me } = useSession();
  const { search } = useLocation();
  const [plans, setPlans] = useState<PlanOption[] | null>(null);
  const [tier, setTier] = useState(search.get("tier") ?? "standard-m");
  const [interval, setIv] = useState<Interval>(search.get("interval") === "year" ? "year" : "month");
  const [code, setCode] = useState<Code | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: ReactNode } | null>(null);

  useEffect(() => {
    if (auth.kind !== "in") return;
    void billing.get().then((r) => r.ok && setPlans(r.data.plans ?? []));
  }, [auth.kind]);

  if (auth.kind === "out") return <Redirect to={`/signup?next=${encodeURIComponent("/plans")}`} />;
  if (auth.kind !== "in" || !me) return <PageSplash />;
  if (!me.billing.enabled) return <Redirect to="/start" />;
  if (me.billing.plan && ["active", "trialing", "past_due", "unpaid"].includes(me.billing.plan.status)) return <Redirect to="/dashboard" />;

  const picked = plans?.find((p) => p.id === tier);
  const free = !!code && code.percentOff === 100 && code.duration === "forever" && (!code.tier || code.tier === tier);
  const full = !!picked && !picked.available;

  const go = async () => {
    if (!picked) return;
    setBusy(true);
    setMsg(null);
    const r = await billing.checkout(picked.id, interval, code?.code);
    if (r.ok) {
      location.assign(r.data.url); // Stripe Checkout; it comes back to /start
      return;
    }
    setBusy(false);
    if (r.body?.full) setMsg({ ok: true, text: <>{picked.name} is full right now. We’ll email {me.email} the moment there’s room.</> });
    else setMsg({ ok: false, text: r.error });
  };

  return (
    <HxPage
      testid="plans"
      top={
        <button className="hx-btn hx-btn-quiet hx-btn-sm" onClick={() => void signOut()}>
          Sign out
        </button>
      }
    >
      <div className="hx-column is-wide hx-plans">
        <div className="hx-column-head hx-rise">
          <h1 className="hx-h1">
            Pick your <em>cloud machine.</em>
          </h1>
          <p>Always on, with Claude Code, Codex, Docker and a desktop ready. Change the size any time; it applies while it runs.</p>
        </div>
        <div className="hx-plans-bar hx-rise" style={{ ["--i" as string]: 1 }}>
          <IntervalSwitch value={interval} onChange={setIv} />
        </div>
        {!plans ? (
          <div className="hx-sizes" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="hx-size is-skeleton" />
            ))}
          </div>
        ) : (
          <SizeCards plans={plans} tier={tier} interval={interval} code={code} art onPick={(id) => (setTier(id), setMsg(null))} />
        )}
        <div className="hx-plans-go hx-rise" style={{ ["--i" as string]: 4 }}>
          <CodeField code={code} onCode={setCode} />
          {msg && (
            <div className={msg.ok ? "hx-note" : "hx-error"} role={msg.ok ? "status" : "alert"} data-testid="plans-msg">
              {msg.text}
            </div>
          )}
          <button className="hx-btn hx-btn-primary hx-btn-block" disabled={busy || !picked} onClick={() => void go()} data-testid="plans-go">
            {busy ? (
              <>
                <span className="hx-spin" aria-hidden="true" /> {full ? "One moment…" : "Opening the payment page…"}
              </>
            ) : full ? (
              "Email me when there’s room"
            ) : free ? (
              "Get it free"
            ) : (
              <>
                Continue to payment <span aria-hidden="true">→</span>
              </>
            )}
          </button>
          <p className="hx-legal">
            {free ? "Free for good: no card needed." : `${interval === "year" ? "Billed yearly" : "Billed monthly"} until you cancel, from your dashboard any time. Payments by Stripe.`}{" "}
            <Link to="/terms">Terms</Link>
          </p>
        </div>
      </div>
    </HxPage>
  );
}
